// next-app/src/store/check-store.ts
import { create } from 'zustand';
import type { Participant, Vehicle } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface CheckState {
  // Check-in state
  checkInParticipants: Participant[];
  checkInVehicles: Vehicle[];
  checkInSearch: string;

  // Check-out state
  checkOutParticipants: Participant[];
  checkOutVehicles: Vehicle[];
  checkOutSearch: string;

  // Selected participant for actions
  selectedParticipant: Participant | null;
}

interface CheckActions {
  setCheckInData: (participants: Participant[], vehicles: Vehicle[]) => void;
  setCheckOutData: (participants: Participant[], vehicles: Vehicle[]) => void;
  setCheckInSearch: (search: string) => void;
  setCheckOutSearch: (search: string) => void;
  setSelectedParticipant: (participant: Participant | null) => void;
  updateParticipant: (participant: Participant) => void;
  reset: () => void;
}

type CheckStore = CheckState & CheckActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: CheckState = {
  checkInParticipants: [],
  checkInVehicles: [],
  checkInSearch: '',
  checkOutParticipants: [],
  checkOutVehicles: [],
  checkOutSearch: '',
  selectedParticipant: null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useCheckStore = create<CheckStore>()((set) => ({
  ...initialState,

  setCheckInData: (participants, vehicles) =>
    set({ checkInParticipants: participants, checkInVehicles: vehicles }),

  setCheckOutData: (participants, vehicles) =>
    set({ checkOutParticipants: participants, checkOutVehicles: vehicles }),

  setCheckInSearch: (search) => set({ checkInSearch: search }),

  setCheckOutSearch: (search) => set({ checkOutSearch: search }),

  setSelectedParticipant: (participant) => set({ selectedParticipant: participant }),

  updateParticipant: (participant) =>
    set((state) => ({
      checkInParticipants: state.checkInParticipants.map((p) =>
        p.participant_id === participant.participant_id ? participant : p
      ),
      checkOutParticipants: state.checkOutParticipants.map((p) =>
        p.participant_id === participant.participant_id ? participant : p
      ),
    })),

  reset: () => set(initialState),
}));

// ============================================================================
// Selectors
// ============================================================================

export const selectCheckInParticipants = (state: CheckStore) => state.checkInParticipants;
export const selectCheckOutParticipants = (state: CheckStore) => state.checkOutParticipants;
export const selectSelectedParticipant = (state: CheckStore) => state.selectedParticipant;
